import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Trophy, Target, CheckCircle2, XCircle, Flame, Mail, Sparkles, Package } from "lucide-react";
import { ShareButton } from "@/components/ShareCard";

export default function Profile() {
  const { user, setUser } = useAuth();
  const [history, setHistory] = useState([]);
  const [streak, setStreak] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSub, setSavingSub] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/me/votes").then((r) => setHistory(r.data)).catch(() => {}),
      api.get("/me/streak").then((r) => setStreak(r.data)).catch(() => {}),
      api.get("/me/purchases").then((r) => setPurchases(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  if (!user) return null;
  const accuracy = user.total_votes ? Math.round((user.correct_votes / user.total_votes) * 100) : 0;

  const toggleSub = async () => {
    setSavingSub(true);
    try {
      const { data } = await api.post("/me/subscribe", { subscribed: !user.subscribed_daily });
      setUser({ ...user, subscribed_daily: data.subscribed_daily });
    } finally {
      setSavingSub(false);
    }
  };

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post("/billing/portal", { return_url: `${window.location.origin}/profile` });
      window.location.href = data.url;
    } catch (e) {
      setPortalBusy(false);
      alert("Could not open billing portal");
    }
  };

  const current = streak?.current ?? 0;
  const longest = streak?.longest ?? 0;
  const nextMilestone = streak?.next_milestone;
  const nextBonus = streak?.next_bonus;

  return (
    <div className="max-w-5xl mx-auto px-6 sm:px-8 md:px-12 py-16" data-testid="profile-page">
      <div className="label-mono mb-3">Your stats</div>
      <div className="flex items-center justify-between gap-4 flex-wrap mb-10">
        <h1 className="font-display font-black uppercase text-4xl md:text-5xl tracking-tighter">
          {user.name}
        </h1>
        <div className="flex items-center gap-3 flex-wrap">
          {user.is_pro && (
            <span className="tag tag-ai flex items-center gap-1.5" data-testid="profile-pro-badge">
              <Sparkles size={12}/> Pro{user.pro_until ? ` · renews ${user.pro_until?.slice(0,10)}` : ""}
            </span>
          )}
          {user.subscription_cancel_at_period_end && (
            <span className="tag tag-wrong" data-testid="profile-sub-cancelled">
              Cancels on {user.pro_until?.slice(0,10)}
            </span>
          )}
          {user.stripe_customer_id && (
            <button className="btn btn-secondary" onClick={openPortal} disabled={portalBusy} data-testid="profile-manage-sub-button">
              {portalBusy ? "…" : "Manage billing"}
            </button>
          )}
          <ShareButton testId="profile-share-button" />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-12">
        <div className="card p-6" data-testid="stat-points">
          <Trophy size={20} style={{ color: "#fbbf24" }} />
          <div className="font-display font-black text-3xl md:text-4xl mt-3">{user.points}</div>
          <div className="label-mono mt-2">Total points</div>
        </div>
        <div className="card p-6" data-testid="stat-accuracy">
          <Target size={20} style={{ color: "var(--ai)" }} />
          <div className="font-display font-black text-3xl md:text-4xl mt-3">{accuracy}%</div>
          <div className="label-mono mt-2">Accuracy</div>
        </div>
        <div className="card p-6" data-testid="stat-votes">
          <div className="text-zinc-500">●</div>
          <div className="font-display font-black text-3xl md:text-4xl mt-3">{user.total_votes}</div>
          <div className="label-mono mt-2">Episodes voted</div>
        </div>
        <div className="card p-6" style={{ borderColor: current > 0 ? "rgba(251,146,60,0.4)" : "var(--border)" }} data-testid="stat-streak">
          <Flame size={20} style={{ color: current > 0 ? "#fb923c" : "var(--text-faint)" }} className={current > 0 ? "pulse-glow" : ""} />
          <div className="font-display font-black text-3xl md:text-4xl mt-3">{current}</div>
          <div className="label-mono mt-2">Day streak{longest > current ? ` · best ${longest}` : ""}</div>
        </div>
      </div>

      {/* Streak progress */}
      <div className="card p-6 mb-10" data-testid="streak-section">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label-mono">Streak progress</div>
            <div className="mt-1 font-medium">
              {current === 0
                ? "Vote on today\u2019s Daily Challenge to start a streak."
                : nextMilestone
                  ? `${nextMilestone - current} day${nextMilestone - current === 1 ? "" : "s"} to a +${nextBonus} pt bonus`
                  : "You've passed every milestone — legend."}
            </div>
          </div>
          <Sparkles size={18} style={{ color: "var(--ai)" }} />
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[
            { day: 3, bonus: 25 },
            { day: 7, bonus: 75 },
            { day: 30, bonus: 200 },
          ].map((m) => {
            const reached = current >= m.day;
            return (
              <div key={m.day} className="border p-4" style={{ borderColor: reached ? "rgba(34,197,94,0.5)" : "var(--border)", background: reached ? "rgba(34,197,94,0.06)" : "transparent" }}>
                <div className="label-mono">Day {m.day}</div>
                <div className="font-display font-bold text-xl mt-1">+{m.bonus} pts</div>
                {reached && <div className="label-mono mt-2" style={{ color: "var(--success)" }}>✓ Unlocked</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Daily email subscription */}
      <div className="card p-6 mb-12 flex items-center justify-between gap-6 flex-wrap" data-testid="subscribe-section">
        <div className="flex items-start gap-4">
          <Mail size={22} style={{ color: "var(--ai)" }} className="mt-1" />
          <div>
            <div className="font-display font-bold text-lg">Daily Challenge email</div>
            <div className="text-sm mt-1" style={{ color: "var(--text-dim)" }}>
              Get tomorrow{"\u2019"}s challenge in your inbox. Keep your streak alive.
            </div>
          </div>
        </div>
        <button
          className={`btn ${user.subscribed_daily ? "btn-secondary" : "btn-ai"}`}
          onClick={toggleSub}
          disabled={savingSub}
          data-testid="subscribe-toggle-button"
        >
          {savingSub ? "…" : user.subscribed_daily ? "✓ Subscribed (tap to opt out)" : "Subscribe"}
        </button>
      </div>

      {/* Purchases */}
      <h2 className="font-display font-bold text-2xl mb-4">My library</h2>
      {purchases.length === 0 ? (
        <div className="card p-6 mb-10 flex items-center justify-between gap-4 flex-wrap">
          <div style={{ color: "var(--text-dim)" }}>
            <Package size={18} className="inline mr-2"/>
            No template packs yet — your downloads will appear here after purchase.
          </div>
          <Link to="/store" className="btn btn-primary" data-testid="profile-browse-store">Browse store</Link>
        </div>
      ) : (
        <div className="card divide-y mb-10" style={{ borderColor: "var(--border)" }} data-testid="purchases-list">
          {purchases.map((p) => (
            <div key={p.session_id} className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center gap-4">
                <Package size={18} style={{ color: "var(--ai)" }} />
                <div>
                  <div className="font-medium">{p.package_name}</div>
                  <div className="label-mono mt-1">
                    {p.package_type === "pro_subscription" ? "Pro · 30 days" : "Template pack"} · ${p.amount.toFixed(2)}
                  </div>
                </div>
              </div>
              {p.package_type === "template_pack" && p.download_url ? (
                <a href={p.download_url} target="_blank" rel="noreferrer" className="btn btn-secondary" data-testid={`download-${p.package_id}`}>
                  Download
                </a>
              ) : (
                <span className="tag tag-correct">✓ Active</span>
              )}
            </div>
          ))}
        </div>
      )}

      <h2 className="font-display font-bold text-2xl mb-4">Voting history</h2>      {loading ? (
        <div className="label-mono">Loading…</div>
      ) : history.length === 0 ? (
        <div className="card p-10 text-center">
          <div style={{ color: "var(--text-dim)" }}>You haven{"\u2019"}t voted yet.</div>
          <Link to="/challenges" className="btn btn-primary mt-4" data-testid="profile-play-button">Play your first</Link>
        </div>
      ) : (
        <div className="card divide-y" style={{ borderColor: "var(--border)" }}>
          {history.map((h) => (
            <Link to={`/challenges/${h.challenge_id}`} key={h.challenge_id} className="flex items-center justify-between px-6 py-4 hover:bg-white/5 transition-colors" data-testid={`history-row-${h.challenge_id}`} style={{ borderColor: "var(--border)" }}>
              <div className="flex items-center gap-4">
                {h.is_correct ? <CheckCircle2 size={18} style={{ color: "var(--success)" }} /> : <XCircle size={18} style={{ color: "var(--human)" }} />}
                <div>
                  <div className="font-medium">{h.challenge_title}</div>
                  <div className="label-mono mt-1">{h.category} · Voted {h.vote}</div>
                </div>
              </div>
              <div className="label-mono">{h.is_correct ? "+10" : "+0"}</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
